import { NotAuthenticated, Timeout } from '@feathersjs/errors';
import type { MonoTypeOperatorFunction } from 'rxjs';
import { timer, throwError, EMPTY } from 'rxjs';
import { catchError, retryWhen, switchMap } from 'rxjs/operators';

// on timeout, retry after a second
export function retryOnTimeout<T>(): MonoTypeOperatorFunction<T> {
  return retryWhen((error$) =>
    error$.pipe(
      switchMap((error) => {
        if (error instanceof Timeout) {
          return timer(1000);
        }

        // re-throw any other error
        return throwError(error);
      }),
    ),
  );
}

/**
 * On NotAuthenticated, re-subscribe after a delay instead of completing.
 *
 * A `.watch().find()` stream can see a *transient* `NotAuthenticated` during a
 * socket reconnect or token-refresh gap even though the session is still valid
 * — the feathers authentication-client re-authenticates automatically on
 * reconnect. Retrying the source `find()` after a short delay then recovers a
 * fresh snapshot and resumes live updates, exactly like a manual page refresh.
 *
 * This replaces {@link completeOnNotAuthenticated}, whose `EMPTY` permanently
 * COMPLETED the stream on the first NotAuthenticated, stranding that surface
 * stale until the user refreshed (SUP-4067 / SUP-4064: speed badge, I/O names,
 * waypoint-in-space icon, custom gripper).
 *
 * A genuinely-expired session is handled separately by the auth error hook
 * (`auth/utils/currentUserID.ts`) and `sessionLifecycle`, which navigate to
 * `/login` and unmount the subscribing components — tearing down the
 * subscription and stopping this retry. Non-auth errors are re-thrown so the
 * downstream `retryOnTimeout` / error handling still applies.
 */
export function retryOnNotAuthenticated<T>(
  delayMs = 1000,
): MonoTypeOperatorFunction<T> {
  return retryWhen((error$) =>
    error$.pipe(
      switchMap((error) => {
        if (error instanceof NotAuthenticated) {
          return timer(delayMs);
        }

        // re-throw any other error
        return throwError(error);
      }),
    ),
  );
}

/**
 * @deprecated Permanently completes the stream on a transient
 * `NotAuthenticated`, which causes the SUP-4067 "stale until refresh" bugs. Use
 * {@link retryOnNotAuthenticated} instead. Retained only for the staleness
 * regression tests that document the old behavior.
 *
 * (Originally: complete without error; the handler in currentUserID.ts
 * navigates back to login.)
 */
export function completeOnNotAuthenticated<T>(): MonoTypeOperatorFunction<T> {
  return catchError((error) => {
    if (error instanceof NotAuthenticated) {
      return EMPTY;
    }

    // re-throw any other error
    return throwError(error);
  });
}
